import { useEffect, useState } from 'react';
import Drawer from '@base/Drawer/Drawer';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Image from '@base/Image';
import { useSelector, useDispatch } from 'react-redux';
import { Fonts, Spacings } from '@styles/index';
import H2 from '@base/H2/H2';
import { fetchLevelsFaqData } from '@actions/educator';
import { getFaqLevelsData } from '@selectors/educator';
import Accordion from '@base/Accordion/Accordion';
import { getIsEducator } from '@selectors/auth';
import LevelStats from './LevelStats';

const FaqContainer = styled.div`
  margin-top: ${Spacings.SPACING_11B};
  margin-bottom: ${Spacings.SPACING_11B};
  grid-column: 1/11;
`;

const AccordionWrapper = styled.div`
  margin-top: ${Spacings.SPACING_11B};
`;

const List = styled.ul`
  padding-left: ${Spacings.SPACING_5B};
`;

const ListItem = styled.li`
  margin-bottom: ${({ $marginBottom }) =>
    $marginBottom ? Spacings.SPACING_1B : 0};
`;

const StyledAccordian = styled(Accordion)`
  &.MuiAccordion-root {
    padding: 0;
    margin: 0;
    box-shadow: none;
    background-color: transparent;
  }
`;

const StyledAccordionSummary = styled(Accordion.Summary)`
  font-weight: 600;
  font-size: ${Fonts.P1};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  margin-left: -${Spacings.SPACING_4B};
  &.MuiAccordionSummary-root {
    color: var(--color-text-primary);
    padding-left: ${Spacings.SPACING_4B};
    &.Mui-expanded {
      padding-left: ${Spacings.SPACING_4B};
    }
  }
  &:hover {
    background-color: ${({ $isExpanded }) =>
      $isExpanded ? 'initial' : 'var(--color-divider)'};
  }
`;

const StyledAccordionDetails = styled(Accordion.Details)`
  font-size: ${Fonts.P1};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  color: var(--color-text-secondary);
`;

const FaqLevels = ({ isOpen, onClose, onPressBack, isPublic }) => {
  const dispatch = useDispatch();
  const faqData = useSelector(getFaqLevelsData);
  const isEducator = useSelector(getIsEducator);
  const [expandedIndex, setExpandedIndex] = useState(0);

  useEffect(() => {
    if (!faqData.length) {
      dispatch(fetchLevelsFaqData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Drawer
      hideCloseButton={!isPublic}
      showBackButton={!isPublic}
      drawerState={isOpen}
      onClose={onClose}
      onPressBack={onPressBack}
    >
      <FaqContainer>
        <H2>About {isEducator ? 'levels' : "Educator's level"}</H2>
        <AccordionWrapper>
          {faqData.map((faq, index) => {
            const isExpanded = expandedIndex === index;
            const handleClick = () => {
              if (isExpanded) {
                setExpandedIndex(-1);
              } else {
                setExpandedIndex(index);
              }
            };

            return (
              <StyledAccordian expanded={isExpanded} key={index.toString()}>
                <StyledAccordionSummary
                  $isExpanded={isExpanded}
                  expandIcon={
                    <Image
                      src={`levels/${isExpanded ? 'minus' : 'plus'}.svg`}
                      alt="expand"
                      width={40}
                      height={40}
                    />
                  }
                  onClick={handleClick}
                >
                  {faq.question}
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  {faq.answer.length === 1 ? (
                    faq.answer[0]
                  ) : (
                    <List>
                      {faq.answer.map((ans, i) => (
                        <ListItem
                          $marginBottom={i !== faq.answer.length - 1}
                          key={i.toString()}
                        >
                          {ans}
                        </ListItem>
                      ))}
                    </List>
                  )}
                  {faq.type === 'level' && <LevelStats />}
                </StyledAccordionDetails>
              </StyledAccordian>
            );
          })}
        </AccordionWrapper>
      </FaqContainer>
    </Drawer>
  );
};

export default FaqLevels;

FaqLevels.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onPressBack: PropTypes.func,
  isPublic: PropTypes.bool
};

FaqLevels.defaultProps = {
  isOpen: false,
  onClose: () => {},
  onPressBack: () => {},
  isPublic: false
};
